'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Chip, Spinner, ModalContent, ModalHeader, ModalFooter, ModalBody } from '@heroui/react';
import { IdView, ResponsiveModal, Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import {
  OnlyFansIc,
  IcReddit,
  InstagramIc,
  IcTikTok,
  TwitterIc,
  IcTg,
  GoogleIc,
  IcYoutube,
  IcAnotherSoc,
} from '../../../../components/icons';
import { AvailableCampaign } from '../types';
import { useIsMobile } from '../../../../hooks/useMobile';
import { useMiddleTruncate } from '../../../../hooks/useMiddleTruncate';

interface CampaignSearchSelectProps {
  placeholder?: string;
  campaigns: AvailableCampaign[];
  selectedKey: string;
  onSelectionChange: (value: string) => void;
  isLoading?: boolean;
  startContent?: React.ReactNode;
}

const CampaignSearchSelect: React.FC<CampaignSearchSelectProps> = ({
  placeholder = 'Кампания',
  campaigns,
  selectedKey,
  onSelectionChange,
  isLoading = false,
  startContent,
}) => {
  const { t } = useTranslations();
  const isMobile = useIsMobile();
  const truncateMiddle = useMiddleTruncate(4);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedCampaign = campaigns.find((c) => c.id === selectedKey);

  // Close dropdown when clicking outside (desktop only)
  useEffect(() => {
    if (isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

  const handleSelect = (campaignId: string) => {
    onSelectionChange(campaignId);
    setIsOpen(false);
  };

  // Get source display name
  const getSourceDisplayName = (source: string) => {
    const sourceMap: Record<string, string> = {
      only_fans: 'OnlyFans',
      onlyfans: 'OnlyFans',
      fansly: 'Fansly',
      reddit: 'Reddit',
      instagram: 'Instagram',
      tiktok: 'TikTok',
      twitter: 'Twitter',
      telegram: 'Telegram',
      google: 'Google',
      youtube: 'YouTube',
    };
    return sourceMap[source?.toLowerCase()] || source || 'OnlyFans';
  };

  // Get source icon
  const getSourceIcon = (source: string) => {
    const sourceIcons: Record<string, React.ReactNode> = {
      only_fans: <OnlyFansIc size={14} />,
      onlyfans: <OnlyFansIc size={14} />,
      reddit: <IcReddit size={14} />,
      instagram: <InstagramIc size={14} fill="#11181C" />,
      tiktok: <IcTikTok size={14} />,
      twitter: <TwitterIc size={14} fill="#11181C" />,
      telegram: <IcTg size={14} />,
      google: <GoogleIc size={14} fill="#11181C" />,
      youtube: <IcYoutube size={14} />,
    };
    return sourceIcons[source?.toLowerCase()] || <IcAnotherSoc size={14} />;
  };

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Get campaign link ending for display
  const getCampaignLinkEnding = (campaign: AvailableCampaign) => {
    const link = campaign.model?.link;
    if (link) {
      const parts = link.split('/');
      return parts[parts.length - 1] || '';
    }
    return campaign.model?.of_user_name || campaign.modelName || '';
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 h-9 px-3 py-1.5 bg-auth-surface rounded-2 hover:bg-[#EEEEEF] transition-colors"
      >
        {startContent}
        {selectedCampaign ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-auth-ink">#{selectedCampaign.shortId.slice(-4)}</span>
            <span className="text-xs font-semibold text-auth-ink">
              {getCampaignLinkEnding(selectedCampaign)}
            </span>
          </div>
        ) : (
          <span className="text-sm text-auth-muted">{placeholder}</span>
        )}
        {/* {isLoading && <Spinner size="sm" className="ml-1" />} */}
      </button>

      {/* Dropdown */}
      {isOpen && !isMobile && (
        <div className="absolute top-[calc(100%+8px)] left-0 z-50 bg-white border border-[#E0E0E0] rounded-3 shadow-[0rem_0rem_1.5rem_0rem_rgba(0,0,0,0.1)] p-2 min-w-[20rem] max-h-[18.75rem] overflow-y-auto">
          {/* "All" option */}
          <button
            type="button"
            onClick={() => handleSelect('')}
            className={`w-full flex items-center h-8 px-2 rounded-2 text-left ${
              selectedKey === '' ? 'bg-[#EEEEEF]' : 'hover:bg-auth-surface'
            }`}
          >
            <Typography variant="body2" className="text-auth-ink">
              {t('partner.revshare.subscribers.filters.allCampaigns') || 'Все кампании'}
            </Typography>
          </button>

          {/* Campaigns list */}
          <div className="flex flex-col mt-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Spinner size="sm" />
              </div>
            ) : campaigns.length === 0 ? (
              <div className="px-2 py-2.5 text-auth-muted text-sm">
                {t('partner.revshare.income.filters.noCampaigns') || 'Нет кампаний'}
              </div>
            ) : (
              campaigns.map((campaign) => (
                <button
                  key={campaign.id}
                  type="button"
                  onClick={() => handleSelect(campaign.id)}
                  className={`w-full flex items-center gap-2 h-8 px-2 py-1.5 rounded-3 text-left ${
                    selectedKey === campaign.id ? 'bg-[#EEEEEF]' : 'hover:bg-auth-surface'
                  }`}
                >
                  <IdView textToCopy={campaign.id}>{campaign.shortId.slice(-4)}</IdView>
                  <span className="text-xs font-semibold text-auth-ink">
                    {getCampaignLinkEnding(campaign).slice(-4)}
                  </span>
                  <span className="text-xxs text-auth-muted">
                    от {formatDate(campaign.createdAt)}
                  </span>
                  <Chip
                    size="sm"
                    variant="bordered"
                    classNames={{
                      base: 'h-6 border-[1.4px] border-auth-strength-none rounded-3.5 px-1',
                      content: 'flex items-center gap-0.5 px-1 text-xs text-auth-ink',
                    }}
                    startContent={getSourceIcon(campaign.source)}
                  >
                    {getSourceDisplayName(campaign.source)} {formatDate(campaign.createdAt)}
                  </Chip>
                </button>
              ))
            )}
          </div>
        </div>
      )}
      {isOpen && isMobile && (
        <ResponsiveModal isOpen={isOpen} onClose={() => setIsOpen(false)} scrollBehavior="inside">
          <ModalContent>
            <ModalHeader className="p-4 pt-10">
              {/* "All" option */}
              <button
                type="button"
                onClick={() => handleSelect('')}
                className={`w-full flex items-center md:h-8 p-2 px-4 rounded-2 text-left ${
                  selectedKey === '' ? 'bg-[#EEEEEF]' : 'hover:bg-auth-surface'
                }`}
              >
                <Typography variant="body2" className="text-auth-ink">
                  {t('partner.revshare.subscribers.filters.allCampaigns') || 'Все кампании'}
                </Typography>
              </button>
            </ModalHeader>
            <ModalBody className="p-4 pt-0">
              {/* Campaigns list */}
              <div className="flex flex-col gap-1">
                {isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                  </div>
                ) : campaigns.length === 0 ? (
                  <div className="px-2 py-2.5 text-auth-muted text-sm">
                    {t('partner.revshare.income.filters.noCampaigns') || 'Нет кампаний'}
                  </div>
                ) : (
                  campaigns.map((campaign) => (
                    <button
                      key={campaign.id}
                      type="button"
                      onClick={() => handleSelect(campaign.id)}
                      className={`w-full flex md:flex-row flex-col md:items-center gap-2 md:h-8 px-2 py-1.5 rounded-3 border border-solid border-[#EEEEEF] text-left ${
                        selectedKey === campaign.id ? 'bg-[#EEEEEF]' : 'hover:bg-auth-surface'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <IdView textToCopy={campaign.id}>{campaign.shortId.slice(-4)}</IdView>
                        <span className="text-xs font-semibold text-auth-ink">
                          {getCampaignLinkEnding(campaign).slice(-4)}
                        </span>
                        <span className="text-xxs text-auth-muted">
                          от {formatDate(campaign.createdAt)}
                        </span>
                      </div>
                      <Chip
                        size="sm"
                        variant="bordered"
                        classNames={{
                          base: 'h-6 border-[1.4px] border-auth-strength-none rounded-3.5 px-1',
                          content: 'flex items-center gap-0.5 px-1 text-xs text-auth-ink',
                        }}
                        startContent={getSourceIcon(campaign.source)}
                      >
                        {getSourceDisplayName(campaign.source)} {formatDate(campaign.createdAt)}
                      </Chip>
                    </button>
                  ))
                )}
              </div>
            </ModalBody>
          </ModalContent>
        </ResponsiveModal>
      )}
    </div>
  );
};

export default CampaignSearchSelect;
